import { useContext, useEffect, useState } from 'react';
import apiFetch from '../../context/api';
import { UserContext } from '../../context/UserContext';

/** Default del sistema (espejo de constants.CAMBIO_CAJA_DEFECTO en el backend). */
export const CAMBIO_CAJA_DEFECTO = 135;

/**
 * Cambio a dejar en la caja al cerrar el turno, EFECTIVO del club: el fijado en
 * Configuración (venues.cambio_caja_defecto) o el default del sistema (135 €). Es
 * un objetivo orientativo —en cada cierre sigue siendo editable—, así que si el GET
 * falla se responde el default: una pantalla operativa no se bloquea por un ajuste.
 *
 * Caché por módulo, con dos reglas que importan en una tablet que pasa el día
 * abierta con la SPA cargada:
 *  - SOLO se cachea una respuesta buena. Si el GET falla (wifi floja, token caducado)
 *    se contesta el default pero la entrada se descarta: el siguiente montaje reintenta
 *    en vez de quedarse con 135 hasta recargar.
 *  - Caduca a los CINCO minutos: si el propietario cambia el valor desde otro
 *    dispositivo, la tablet lo recoge sin esperar a un reinicio. (`invalidarCambioCaja`
 *    solo alcanza a la pestaña que guarda.)
 * apiFetch y no fetch a pelo: en prod FE y API son dominios distintos.
 */
const TTL_MS = 5 * 60 * 1000;
const cache = new Map(); // venueID → { desde: number, promesa: Promise<number> }

function fetchCambio(venueID, token) {
  const hit = cache.get(venueID);
  if (hit && Date.now() - hit.desde < TTL_MS) return hit.promesa;

  const promesa = apiFetch(`/v1/venues/${venueID}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error('venue no disponible'))))
    .then((v) => {
      // `!= null` y no truthiness: 0 es un valor válido (club que no deja cambio).
      const n = v?.cambio_caja_defecto != null ? Number(v.cambio_caja_defecto) : NaN;
      return Number.isFinite(n) && n >= 0 ? n : CAMBIO_CAJA_DEFECTO;
    })
    .catch(() => {
      // No se cachea el fallo (ver arriba). Solo se borra si la entrada sigue siendo ESTA.
      if (cache.get(venueID)?.promesa === promesa) cache.delete(venueID);
      return CAMBIO_CAJA_DEFECTO;
    });
  cache.set(venueID, { desde: Date.now(), promesa });
  return promesa;
}

/** Invalida la caché (la usa Configuración al guardar un cambio nuevo). */
export function invalidarCambioCaja(venueID) {
  cache.delete(venueID);
}

export default function useCambioCaja() {
  const ctx = useContext(UserContext) || [];
  const token = ctx[0];
  const venueID = ctx[3];
  const [cambio, setCambio] = useState(CAMBIO_CAJA_DEFECTO);

  useEffect(() => {
    if (!token || !venueID || typeof fetch !== 'function') return undefined;
    let vivo = true;
    fetchCambio(venueID, token).then((n) => {
      if (vivo) setCambio(n);
    });
    return () => {
      vivo = false;
    };
  }, [token, venueID]);

  return cambio;
}
